// Moves enemy missiles. ICBMs fall straight; MIRV warheads split into children
// at their split altitude; smart bombs steer away from active blasts (but keep
// descending). All RNG draws thread through state for determinism.

import type { EnemyMissile, Explosion, Vec2 } from '../state/types';
import type { IntegrateEnemies } from './contracts';
import { add, direction, len, normalize, scale } from '../geometry/vec2';
import { intInRange } from '../rng/mulberry32';
import { aliveTargets } from '../state/targets';
import { waveConfig } from '../waves/waveConfig';
import {
  FIXED_DT,
  MIRV_SPLIT_COUNT,
  SMARTBOMB_EVADE_RANGE,
  SMARTBOMB_EVADE_STRENGTH,
} from '../constants';

/** Steer a smart bomb's velocity away from nearby active blasts, keeping it
 *  descending. Deterministic (no RNG). Returns the unchanged velocity if clear. */
function evadeVelocity(pos: Vec2, vel: Vec2, explosions: readonly Explosion[]): Vec2 {
  let ax = 0;
  let ay = 0;
  let threatened = false;
  for (const ex of explosions) {
    if (ex.radius <= 0) continue;
    const dx = pos.x - ex.pos.x;
    const dy = pos.y - ex.pos.y;
    const d = Math.hypot(dx, dy);
    if (d > 0 && d < ex.radius + SMARTBOMB_EVADE_RANGE) {
      ax += dx / d;
      ay += dy / d;
      threatened = true;
    }
  }
  if (!threatened) return vel;

  const speed = len(vel);
  const dir = normalize(vel);
  const steered = normalize({
    x: dir.x + ax * SMARTBOMB_EVADE_STRENGTH,
    y: dir.y + ay * SMARTBOMB_EVADE_STRENGTH,
  });
  // Keep a real downward component so evasion dodges sideways, never stalls or
  // flies upward. Floor the (normalized) y, then renormalize.
  const MIN_DESCENT = 0.5;
  const finalDir =
    steered.y < MIN_DESCENT ? normalize({ x: steered.x, y: MIN_DESCENT }) : steered;
  return scale(finalDir, speed);
}

export const integrateEnemies: IntegrateEnemies = (state) => {
  if (state.enemyMissiles.length === 0) return state;

  const speed = waveConfig(state.wave).speed;
  const targets = aliveTargets(state);
  let rng = state.rng;
  let nextId = state.nextEntityId;
  const out: EnemyMissile[] = [];

  for (const e of state.enemyMissiles) {
    const vel =
      e.kind === 'smartBomb'
        ? evadeVelocity(e.pos, e.vel, state.explosions)
        : e.vel;
    const pos = add(e.pos, scale(vel, FIXED_DT));

    if (e.splitAt !== null && pos.y >= e.splitAt) {
      // MIRV: replace the warhead with independently-targeted children.
      // aliveTargets is never empty (it falls back to all city positions).
      for (let i = 0; i < MIRV_SPLIT_COUNT; i++) {
        const pick = intInRange(rng, 0, targets.length);
        rng = pick.state;
        const target = targets[pick.value] ?? e.target;
        const dir = direction(pos, target);
        const vel = dir.x === 0 && dir.y === 0 ? { x: 0, y: speed } : scale(dir, speed);
        out.push({ id: nextId++, kind: 'icbm', origin: pos, pos, target, vel, splitAt: null });
      }
    } else {
      out.push({ ...e, pos, vel });
    }
  }

  return { ...state, enemyMissiles: out, rng, nextEntityId: nextId };
};
